import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldNumberArray,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS, OFFSET_ARRAY } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genBrandField, genSchema } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { GroupingFormBrandType } from './groupingTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.grouping.style, {
  group: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  answer: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  icon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.grouping.schema.answerIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  icon_hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  progressResult__var: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 1, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.progressCompleted'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  progress: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.progressBackground'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
  },
  group_effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.grouping.schema.group_boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  answer_effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.grouping.schema.answer_boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  indents: {
    padding: genBrandField({
      as: FieldNumberArray,
      label: t('elements.grouping.schema.cardIndents'),
      layout: 'vertical',
      props: { options: OFFSET_ARRAY },
    }),
    gap: genBrandField({
      as: FieldNumber,
      label: t('elements.grouping.schema.answerGap'),
      props: { width: 64 },
    }),
    marginTop: genBrandField({
      as: FieldNumber,
      label: t('elements.grouping.schema.groupsGap'),
      props: { width: 64 },
    }),
  },
})

const GroupingFormStyle: GroupingFormBrandType = ({ device, subConfigs, edited, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  const handleSetFeedback = useCallback(() => setForm?.(BrandFormEnum.FEEDBACK), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <Typography weight='medium'>{t('elements.grouping.tags.group')}</Typography>
        <FormField config={schema.group.backgroundColor} />
        <FormField config={schema.group.borderRadius} />
        <FormField config={schema.group.borderColor} />
        <FormField config={schema.group.borderWidth} />

        <Typography weight='medium'>{t('elements.grouping.tags.answer')}</Typography>
        <FormField config={schema.answer.backgroundColor} />
        <FormField config={schema.answer.borderRadius} />
        <FormField config={schema.answer.borderStyle} />
        <FormField config={schema.answer.borderColor} />
        <FormField config={schema.answer.borderWidth} />

        <Typography weight='medium'>{t('elements.grouping.tags.icon')}</Typography>
        <FormField config={schema.icon.backgroundImage} />
        <FormField config={schema.icon.color} />
        <FormField config={schema.icon.backgroundColor} />
        <FormField config={schema.icon_hover.backgroundColor} />

        <Typography weight='medium'>{t('elements.grouping.tags.progress')}</Typography>
        <FormField config={schema.progressResult__var.borderRadius} />
        <FormField config={schema.progressResult__var.backgroundColor} />
        <FormField config={schema.progress.backgroundColor} />
        <FormField config={schema.progress.height} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.group_effects.boxShadow} />
        <FormField config={schema.answer_effects.boxShadow} />

        <Typography weight='medium'>{t('elements.grouping.tags.indents')}</Typography>
        <FormField config={schema.indents.padding} />
        <FormField config={schema.indents.gap} />
        <FormField config={schema.indents.marginTop} />

        <TileButton
          description={t('uiKit.button.feedbackDescription')}
          icon='otherRight'
          name='showFeedbackForm'
          onClick={handleSetFeedback}
        >
          <Typography weight='medium'>{t('page.brand.tab.feedback')}</Typography>
          {isAnyEdited(edited.feedback) && <BrandTagEdited />}
        </TileButton>

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(GroupingFormStyle) as typeof GroupingFormStyle
